import React, { useMemo, useState } from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import { DiscourseSubItemType } from './DiscourseHistoryItem';
import useTranslation from '../../../../Hooks/useTranslation';
import AttachmentButton from '../../../../Components/Button/AttachmentButton';
import { PreviewFile } from '../../../../Components/MediaViewer/MediaViewer';
import InsuranceReportDetailModal from '../../../Insurance/Component/InsuranceReportDetail/InsuranceReportDetailModal';

interface DiscourseSubItemProps {
	item: DiscourseSubItemType;
	key: string;
	patientId: string;
	virtualPatientId: string;
	onShowInCalendar?: any;
}

const DiscourseHistorySubItem: React.FC<DiscourseSubItemProps> = ({
	item,
	patientId,
	virtualPatientId,
	onShowInCalendar,
}) => {
	const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
	const [isInsuranceReportOpened, setIsInsuranceReportOpened] = useState(false);
	const { socketUrl, links, globalTemplate } = useAppContext();
	const { t } = useTranslation();

	const itemName = useMemo(() => {
		let name;

		if (item?.extra?.text) {
			return item?.extra?.text;
		}

		if (item?.extra?.title) {
			return item?.extra?.title;
		}

		switch (item.type) {
			case 'medicalReportPatient':
				name = 'base.shared.medicalReport';
				break;
			case 'medicalReport':
				name = 'base.shared.medicalExamination';
				break;
			case 'prescription':
				name = 'base.ePrescription';
				break;
			case 'appointment':
				name = 'base.appointment';
				break;
		}
		return t(name);
	}, [item]);

	const additionalText = useMemo(() => {
		if (item?.extra?.sentToPatient) {
			return t('component.medicalReport.sentToPatient');
		}

		return undefined;
	}, [item]);

	const fileToViewer: PreviewFile = useMemo(() => {
		let fileUrl = socketUrl + '/document/' + item.extra.id + '/pdf/download';

		if (item.type === 'prescription' && item.extra.id) {
			const prescriptionLink =
				globalTemplate === 'default'
					? links.treatment.downloadPrescriptionSukl
					: links.treatment.downloadPrescription;
			fileUrl = prescriptionLink
				.replace('__PATIENT__', patientId)
				.replace('__ID__', item.extra.id);
		}

		if (item.type === 'documentationFile') {
			fileUrl = `${socketUrl}/patients/${virtualPatientId}/documentation/${item.extra.folderId}/${item.extra.id}`;
		}

		return {
			id: item.extra.id ?? '',
			displayName: itemName ?? '',
			type: 'pdf',
			preview: fileUrl,
			download: fileUrl,
		};
	}, [item]);

	const handleItem = () => {
		switch (item.type) {
			case 'appointment':
				onShowInCalendar?.('order', { params: { eventId: item.extra.id } });
				break;
			case 'insuranceReport':
				setIsInsuranceReportOpened(true);
				break;
			default:
				setMediaViewerOpened(true);
				break;
		}
	};

	return (
		<React.Fragment>
			<AttachmentButton
				onClick={handleItem}
				title={itemName}
				additionalText={additionalText}
			/>
			<MediaViewerModal
				opened={mediaViewerOpened}
				onClose={() => setMediaViewerOpened(false)}
				file={fileToViewer}
			/>
			{item.type === 'insuranceReport' &&
				item.extra.id &&
				isInsuranceReportOpened && (
					<InsuranceReportDetailModal
						insuranceReportId={item.extra.id}
						isOpen
						onClose={() => setIsInsuranceReportOpened(false)}
					/>
				)}
		</React.Fragment>
	);
};

export default DiscourseHistorySubItem;
